import { CASE_001, evidenceById, statementById } from '../case/case001';
import {
  advanceYuragiAgents,
  createYuragiAgentSystem,
  verifyYuragiSystem,
  YURAGI_COUPLING,
  yuragiAgentTicks,
  yuragiCrisisReady,
  type CourtAgentStimulus,
} from '../engine/courtAgents';
import type {
  CaseOutcome,
  CharacterId,
  CounselProfile,
  CourtEventState,
  GameAction,
  GameChapter,
  GameState,
  PlayerActionRecord,
  TheoryKey,
  YuragiAgentSystem,
} from './types';

const DEFAULT_PLAYER = {
  position: [0, 1.68, 8] as [number, number, number],
  yaw: 0,
  pitch: 0,
};

const WITNESSES: CharacterId[] = ['lyra', 'rook', 'iona', 'silas'];

const COURT_EVENTS: Partial<Record<string, Omit<CourtEventState, 'triggerStatementId'>>> = {
  'lyra-never-entered': { id: 'lyra-confession', elapsedMs: 0, durationMs: 4600 },
  'silas-recipient': { id: 'silas-outburst', elapsedMs: 0, durationMs: 5000 },
  'iona-synchronized': { id: 'iona-breaks', elapsedMs: 0, durationMs: 4700 },
  'rook-no-pulse': { id: 'rook-rage', elapsedMs: 0, durationMs: 5200 },
  'rook-main-door': { id: 'rook-lunge', elapsedMs: 0, durationMs: 7600 },
};

const emptyTheory = () => ({
  culprit: null,
  method: null,
  motive: null,
  doubt: null,
});

export function createDefaultProfile(): CounselProfile {
  return {
    created: false,
    counselId: 'EC-UNREGISTERED',
    name: '',
    callsign: 'NIGHT COUNSEL',
    avatar: 'cipher',
    accent: '#c99b58',
    createdAt: null,
    xp: 0,
    level: 1,
    casesCompleted: 0,
    sRanks: 0,
    bestScore: 0,
    truthRecovered: 0,
    recordedResultHashes: [],
  };
}

export function createInitialState(): GameState {
  return {
    schemaVersion: 4,
    phase: 'title',
    chapter: 'intake',
    location: 'archive',
    overlay: 'none',
    selectedEvidenceId: null,
    activeCharacterId: null,
    activeStatementIndex: 0,
    discoveredEvidenceIds: CASE_001.evidence.filter((item) => item.initial).map((item) => item.id),
    viewedEvidenceIds: [],
    pressedStatementIds: [],
    pressureByStatement: {},
    questionedCharacterIds: [],
    contradictionsFound: [],
    credibility: 3,
    focus: 5,
    theory: emptyTheory(),
    outcome: null,
    revealElapsedMs: 0,
    player: DEFAULT_PLAYER,
    actionLog: [],
    startedAt: null,
    elapsedMs: 0,
    courtEvent: null,
    notice: null,
    profile: createDefaultProfile(),
    audio: {
      master: 0.82,
      music: 0.58,
      voice: 0.9,
      sfx: 0.82,
      subtitles: true,
    },
    muted: false,
    reducedMotion: false,
    yuragi: createYuragiAgentSystem(),
    yuragiClockMs: 0,
  };
}

function counselActor(state: GameState): string {
  return state.profile.created ? state.profile.counselId : 'player-counsel';
}

function courtCoupling(state: GameState): number {
  if (state.courtEvent) return YURAGI_COUPLING.crisis;
  if (state.phase === 'trial' || state.phase === 'theory') return YURAGI_COUPLING.trial;
  if (state.phase === 'aftermath') return YURAGI_COUPLING.aftermath;
  return YURAGI_COUPLING.investigation;
}

function evolve(
  state: GameState,
  stimuli: CourtAgentStimulus[],
  coupling = courtCoupling(state),
): YuragiAgentSystem {
  return advanceYuragiAgents(state.yuragi, stimuli, { coupling });
}

function record(
  state: GameState,
  type: string,
  target?: string,
  value?: string,
): PlayerActionRecord[] {
  return [
    ...state.actionLog,
    {
      type,
      target,
      value,
      sequence: state.actionLog.length + 1,
    },
  ];
}

function hashResult(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0').toUpperCase();
}

function routeTitle(state: GameState): string {
  if (state.credibility === 3 && state.contradictionsFound.length === CASE_001.statements.length) {
    return 'THE UNBROKEN RECORD';
  }
  if (state.contradictionsFound.length >= 4) return 'THE CLOCKBREAKER';
  if (state.credibility <= 1) return 'THE RAZOR VERDICT';
  return 'THE REASONABLE SHADOW';
}

function outcomeFor(state: GameState): CaseOutcome {
  const weights: Record<TheoryKey, number> = {
    culprit: 30,
    method: 20,
    motive: 20,
    doubt: 20,
  };

  const theoryScore = CASE_001.theory.reduce((score, section) => {
    return score + (state.theory[section.key] === section.correctId ? weights[section.key] : 0);
  }, 0);
  const contradictionScore = Math.min(10, state.contradictionsFound.length * 2);
  const score = Math.min(100, theoryScore + contradictionScore);

  const rank: CaseOutcome['rank'] =
    score >= 95 ? 'S' : score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';
  const verdict: CaseOutcome['verdict'] =
    score >= 80
      ? 'NOT GUILTY'
      : score >= 60
        ? 'REASONABLE DOUBT'
        : score >= 40
          ? 'HUNG VERDICT'
          : 'CONVICTED';

  const semanticLog = state.actionLog
    .filter((item) => item.type !== 'tick' && item.type !== 'move')
    .map((item) => `${item.type}:${item.target ?? ''}:${item.value ?? ''}`)
    .join('|');
  const theory = CASE_001.theory.map((item) => `${item.key}:${state.theory[item.key] ?? ''}`).join('|');
  const resultHash = hashResult(
    `${CASE_001.seed}|${theory}|${[...state.contradictionsFound].sort().join(',')}|${semanticLog}|${state.yuragi.transitionHash}`,
  );

  return {
    score,
    rank,
    verdict,
    truthFound: CASE_001.theory.every((item) => state.theory[item.key] === item.correctId),
    clientProtected: score >= 60 && state.theory.doubt === 'clock-drift',
    resultHash,
    completedAt: new Date().toISOString(),
    evidenceFound: state.discoveredEvidenceIds.length,
    contradictions: state.contradictionsFound.length,
    elapsedMs: state.elapsedMs,
    xpAwarded: 180 + score * 4 + state.contradictionsFound.length * 20,
    routeTitle: routeTitle(state),
    yuragiProofHash: state.yuragi.transitionHash,
    yuragiReplayVerified: verifyYuragiSystem(state.yuragi),
    yuragiSyncOrder: state.yuragi.syncOrder,
    yuragiAgentTicks: yuragiAgentTicks(state.yuragi),
  };
}

function clampedStatement(index: number): number {
  return Math.max(0, Math.min(CASE_001.statements.length - 1, index));
}

function investigationChapter(state: GameState, evidenceCount = state.discoveredEvidenceIds.length): GameChapter {
  if (state.questionedCharacterIds.length >= 2 || evidenceCount >= 6) return 'witness-interviews';
  if (evidenceCount >= 3) return 'evidence-sweep';
  return 'scene-lockdown';
}

export function isTrialReady(state: GameState): boolean {
  return state.discoveredEvidenceIds.length >= 6 && state.questionedCharacterIds.length >= 3;
}

function eventFor(statementId: string, yuragi: YuragiAgentSystem): CourtEventState | null {
  const definition = COURT_EVENTS[statementId];
  if (statementId === 'rook-main-door' && definition && !yuragiCrisisReady(yuragi, 'rook')) {
    return { id: 'rook-rage', elapsedMs: 0, durationMs: 5200, triggerStatementId: statementId };
  }
  return definition ? { ...definition, triggerStatementId: statementId } : null;
}

function rewardProfile(profile: CounselProfile, outcome: CaseOutcome): CounselProfile {
  if (profile.recordedResultHashes.includes(outcome.resultHash)) return profile;
  const xp = profile.xp + outcome.xpAwarded;
  return {
    ...profile,
    xp,
    level: Math.max(1, Math.floor(xp / 900) + 1),
    casesCompleted: profile.casesCompleted + 1,
    sRanks: profile.sRanks + Number(outcome.rank === 'S'),
    bestScore: Math.max(profile.bestScore, outcome.score),
    truthRecovered: profile.truthRecovered + Number(outcome.truthFound),
    recordedResultHashes: [outcome.resultHash, ...profile.recordedResultHashes].slice(0, 24),
  };
}

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'START_CASE':
      return {
        ...state,
        phase: 'briefing',
        chapter: 'intake',
        overlay: 'none',
        notice: null,
        startedAt: state.startedAt ?? new Date().toISOString(),
        actionLog: record(state, 'start-case', CASE_001.id),
        yuragi: evolve(state, [{
          targets: 'all',
          kind: 'observe',
          label: 'Case mandate entered into the shared record',
          actorId: counselActor(state),
          subject: CASE_001.id,
          predicate: 'charge',
          value: CASE_001.charge,
          intensity: 0.42,
        }], YURAGI_COUPLING.investigation),
      };

    case 'OPEN_PROFILE':
      return { ...state, overlay: 'profile' };

    case 'SAVE_PROFILE': {
      const name = action.profile.name.trim().slice(0, 32) || 'Anonymous Counsel';
      const callsign = action.profile.callsign.trim().slice(0, 22).toUpperCase() || 'NIGHT COUNSEL';
      const counselId = state.profile.created
        ? state.profile.counselId
        : `EC-${hashResult(`${name}|${callsign}`).slice(0, 6)}`;
      return {
        ...state,
        overlay: 'none',
        profile: {
          ...state.profile,
          ...action.profile,
          name,
          callsign,
          counselId,
          created: true,
          createdAt: state.profile.createdAt ?? new Date().toISOString(),
        },
        actionLog: record(state, 'register-counsel', counselId),
      };
    }

    case 'ENTER_ARCHIVE':
      return {
        ...state,
        phase: 'investigation',
        chapter: 'scene-lockdown',
        location: 'archive',
        overlay: 'none',
        notice: 'Archive Nine unlocked. Preserve the physical record.',
        player: DEFAULT_PLAYER,
        actionLog: record(state, 'enter-location', 'archive'),
        yuragi: evolve(state, [{
          targets: ['lyra', 'rook', 'iona', 'silas'],
          kind: 'outcome',
          label: 'Archive Nine entered under preservation order',
          actorId: counselActor(state),
          subject: 'archive-nine',
          predicate: 'preservation-status',
          value: 'locked',
          intensity: 0.36,
        }], YURAGI_COUPLING.investigation),
      };

    case 'DISCOVER_EVIDENCE': {
      if (!evidenceById[action.evidenceId]) return state;
      const evidence = evidenceById[action.evidenceId];
      const alreadyFound = state.discoveredEvidenceIds.includes(action.evidenceId);
      const discoveredEvidenceIds = alreadyFound
        ? state.discoveredEvidenceIds
        : [...state.discoveredEvidenceIds, action.evidenceId];
      return {
        ...state,
        chapter: investigationChapter(state, discoveredEvidenceIds.length),
        overlay: 'evidence',
        selectedEvidenceId: action.evidenceId,
        discoveredEvidenceIds,
        viewedEvidenceIds: state.viewedEvidenceIds.includes(action.evidenceId)
          ? state.viewedEvidenceIds
          : [...state.viewedEvidenceIds, action.evidenceId],
        notice: alreadyFound ? null : 'Evidence preserved in the court ledger.',
        actionLog: alreadyFound
          ? record(state, 'review-evidence', action.evidenceId)
          : record(state, 'discover-evidence', action.evidenceId),
        yuragi: alreadyFound ? state.yuragi : evolve(state, [{
          targets: 'all',
          kind: 'observe',
          label: `${evidence.title} preserved in the court ledger`,
          actorId: counselActor(state),
          subject: evidence.id,
          predicate: 'evidentiary-record',
          value: `${evidence.reliability}:${evidence.summary}`,
          intensity: evidence.reliability === 'verified' ? 0.62 : 0.45,
        }], YURAGI_COUPLING.investigation),
      };
    }

    case 'VIEW_EVIDENCE':
      if (!state.discoveredEvidenceIds.includes(action.evidenceId)) return state;
      return {
        ...state,
        overlay: 'evidence',
        selectedEvidenceId: action.evidenceId,
        viewedEvidenceIds: state.viewedEvidenceIds.includes(action.evidenceId)
          ? state.viewedEvidenceIds
          : [...state.viewedEvidenceIds, action.evidenceId],
        actionLog: record(state, 'view-evidence', action.evidenceId),
      };

    case 'SELECT_EVIDENCE':
      if (!state.discoveredEvidenceIds.includes(action.evidenceId)) return state;
      return {
        ...state,
        selectedEvidenceId: action.evidenceId,
        actionLog: record(state, 'select-evidence', action.evidenceId),
      };

    case 'START_DIALOGUE': {
      const firstStatement = CASE_001.statements.findIndex(
        (item) => item.characterId === action.characterId,
      );
      const questionedCharacterIds = WITNESSES.includes(action.characterId) &&
        !state.questionedCharacterIds.includes(action.characterId)
        ? [...state.questionedCharacterIds, action.characterId]
        : state.questionedCharacterIds;
      const nextState = { ...state, questionedCharacterIds };
      return {
        ...nextState,
        chapter: state.phase === 'investigation' ? investigationChapter(nextState) : state.chapter,
        overlay: 'dialogue',
        activeCharacterId: action.characterId,
        activeStatementIndex: firstStatement >= 0 ? firstStatement : state.activeStatementIndex,
        notice: null,
        actionLog: record(state, 'question-character', action.characterId),
        yuragi: evolve(state, [{
          targets: [action.characterId],
          kind: 'converse',
          label: `Counsel opened questioning with ${action.characterId}`,
          actorId: counselActor(state),
          intensity: state.phase === 'trial' ? 0.54 : 0.34,
        }]),
      };
    }

    case 'PRESS_STATEMENT': {
      if (!statementById[action.statementId]) return state;
      const currentPressure = state.pressureByStatement[action.statementId] ?? 0;
      const nextPressure = Math.min(3, currentPressure + 1);
      const alreadyPressed = currentPressure > 0;
      const statement = statementById[action.statementId];
      const pressureStimuli: CourtAgentStimulus[] = [{
        targets: [statement.characterId],
        kind: 'converse',
        label: `Statement ${action.statementId} pressed to level ${nextPressure}`,
        actorId: counselActor(state),
        intensity: 0.42 + nextPressure * 0.16,
      }];
      if (nextPressure === 3) {
        pressureStimuli.push({
          targets: [statement.characterId, 'judge'],
          kind: 'outcome',
          label: `Witness committed under maximum pressure: ${action.statementId}`,
          actorId: counselActor(state),
          subject: action.statementId,
          predicate: 'pressure-level',
          value: 3,
          intensity: 0.88,
        });
      }
      return {
        ...state,
        pressedStatementIds: alreadyPressed
          ? state.pressedStatementIds
          : [...state.pressedStatementIds, action.statementId],
        pressureByStatement: {
          ...state.pressureByStatement,
          [action.statementId]: nextPressure,
        },
        focus: alreadyPressed ? state.focus : Math.max(0, state.focus - 1),
        notice: nextPressure === 3 ? 'Pressure threshold reached. The witness has committed to the detail.' : null,
        actionLog: record(state, 'press-statement', action.statementId, String(nextPressure)),
        yuragi: evolve(
          state,
          pressureStimuli,
          nextPressure === 3 && state.phase === 'trial'
            ? YURAGI_COUPLING.crisis
            : courtCoupling(state),
        ),
      };
    }

    case 'SET_STATEMENT': {
      const index = clampedStatement(action.index);
      return {
        ...state,
        activeStatementIndex: index,
        activeCharacterId: CASE_001.statements[index].characterId,
        selectedEvidenceId: null,
        notice: null,
        actionLog: record(state, 'navigate-statement', CASE_001.statements[index].id),
      };
    }

    case 'PRESENT_EVIDENCE': {
      const statement = statementById[action.statementId];
      if (!statement || !state.discoveredEvidenceIds.includes(action.evidenceId)) return state;
      const correct = statement.contradictionEvidenceId === action.evidenceId;
      const alreadyFound = state.contradictionsFound.includes(action.statementId);
      const evidence = evidenceById[action.evidenceId];
      const presentationStimuli: CourtAgentStimulus[] = correct
        ? [
            {
              targets: [statement.characterId, 'judge', 'bailiff'],
              kind: 'teach',
              label: `${evidence.title} contradicted ${action.statementId}`,
              actorId: counselActor(state),
              subject: evidence.id,
              predicate: 'contradicts',
              value: action.statementId,
              intensity: 0.92,
            },
            {
              targets: 'all',
              kind: 'observe',
              label: `Material contradiction entered: ${action.statementId}`,
              actorId: counselActor(state),
              subject: action.statementId,
              predicate: 'court-status',
              value: 'contradicted',
              intensity: 0.74,
            },
          ]
        : [{
            targets: [statement.characterId, 'judge'],
            kind: 'outcome',
            label: `Unsupported exhibit offered against ${action.statementId}`,
            actorId: counselActor(state),
            subject: action.statementId,
            predicate: 'presentation-result',
            value: 'overruled',
            intensity: 0.58,
          }];
      const yuragi = evolve(
        state,
        presentationStimuli,
        correct && !alreadyFound ? YURAGI_COUPLING.crisis : YURAGI_COUPLING.trial,
      );
      const courtEvent = correct && !alreadyFound ? eventFor(action.statementId, yuragi) : null;

      return {
        ...state,
        overlay: courtEvent ? 'none' : 'dialogue',
        selectedEvidenceId: action.evidenceId,
        contradictionsFound:
          correct && !alreadyFound
            ? [...state.contradictionsFound, action.statementId]
            : state.contradictionsFound,
        credibility: correct ? state.credibility : Math.max(0, state.credibility - 1),
        courtEvent,
        notice: correct ? 'Contradiction entered into the permanent record.' : null,
        actionLog: record(
          state,
          correct ? 'expose-contradiction' : 'wrong-presentation',
          action.statementId,
          action.evidenceId,
        ),
        yuragi,
      };
    }

    case 'SKIP_COURT_EVENT':
      return {
        ...state,
        courtEvent: null,
        overlay: state.phase === 'trial' ? 'dialogue' : 'none',
        yuragi: evolve(state, [{
          targets: 'all',
          kind: 'outcome',
          label: 'The bench restored order after a courtroom escalation',
          actorId: 'judge',
          subject: 'tribunal',
          predicate: 'order',
          value: 'restored',
          intensity: 0.66,
        }], YURAGI_COUPLING.trial),
      };

    case 'OPEN_DOSSIER':
      return { ...state, overlay: 'dossier', actionLog: record(state, 'open-dossier') };

    case 'OPEN_SETTINGS':
      return { ...state, overlay: 'settings' };

    case 'CLOSE_OVERLAY':
      return {
        ...state,
        overlay: 'none',
        selectedEvidenceId: null,
      };

    case 'START_TRIAL':
      if (!isTrialReady(state)) {
        return {
          ...state,
          notice: `Tribunal locked — preserve ${Math.max(0, 6 - state.discoveredEvidenceIds.length)} more records and question ${Math.max(0, 3 - state.questionedCharacterIds.length)} more witnesses.`,
        };
      }
      return {
        ...state,
        phase: 'trial',
        chapter: 'tribunal',
        location: 'court',
        overlay: 'dialogue',
        activeStatementIndex: 0,
        activeCharacterId: CASE_001.statements[0].characterId,
        notice: 'The Tribunal of Echoes is now in session.',
        player: DEFAULT_PLAYER,
        actionLog: record(state, 'convene-tribunal'),
        yuragi: evolve(state, [{
          targets: 'all',
          kind: 'outcome',
          label: 'The Tribunal of Echoes convened under oath',
          actorId: 'judge',
          subject: CASE_001.id,
          predicate: 'trial-status',
          value: 'in-session',
          intensity: 0.68,
        }], YURAGI_COUPLING.trial),
      };

    case 'OPEN_THEORY':
      if (state.contradictionsFound.length < 3) {
        return { ...state, notice: 'The court needs at least three exposed contradictions before reconstruction.' };
      }
      return {
        ...state,
        phase: 'theory',
        chapter: 'reconstruction',
        overlay: 'none',
        notice: null,
        actionLog: record(state, 'open-theory'),
        yuragi: evolve(state, [{
          targets: 'all',
          kind: 'outcome',
          label: 'The court closed testimony and opened reconstruction',
          actorId: 'judge',
          subject: CASE_001.id,
          predicate: 'trial-stage',
          value: 'reconstruction',
          intensity: 0.58,
        }], YURAGI_COUPLING.trial),
      };

    case 'SET_THEORY':
      return {
        ...state,
        theory: { ...state.theory, [action.key]: action.optionId },
        actionLog: record(state, 'set-theory', action.key, action.optionId),
      };

    case 'SUBMIT_THEORY': {
      if (Object.values(state.theory).some((value) => value === null)) return state;
      const yuragi = evolve(state, [{
        targets: 'all',
        kind: 'outcome',
        label: 'Counsel submitted a complete causal reconstruction',
        actorId: counselActor(state),
        subject: CASE_001.id,
        predicate: 'theory-submitted',
        value: state.theory,
        intensity: 0.8,
      }], YURAGI_COUPLING.trial);
      const submitted = {
        ...state,
        phase: 'reveal' as const,
        chapter: 'reconstruction' as const,
        location: 'archive' as const,
        overlay: 'none' as const,
        revealElapsedMs: 0,
        notice: null,
        actionLog: record(state, 'submit-theory'),
        yuragi,
      };
      return { ...submitted, outcome: outcomeFor(submitted) };
    }

    case 'TICK': {
      const ms = Math.max(0, action.ms);
      const eventElapsed = state.courtEvent ? state.courtEvent.elapsedMs + ms : 0;
      const eventFinished = Boolean(state.courtEvent && eventElapsed >= state.courtEvent.durationMs);
      const clock = state.yuragiClockMs + ms;
      const yuragiSteps = Math.floor(clock / 3000);
      const coupling = eventFinished
        ? YURAGI_COUPLING.trial
        : courtCoupling(state);
      return {
        ...state,
        elapsedMs: state.elapsedMs + ms,
        revealElapsedMs: state.phase === 'reveal' ? state.revealElapsedMs + ms : state.revealElapsedMs,
        courtEvent: state.courtEvent && !eventFinished
          ? { ...state.courtEvent, elapsedMs: eventElapsed }
          : null,
        overlay: eventFinished && state.phase === 'trial' ? 'dialogue' : state.overlay,
        yuragi: yuragiSteps > 0
          ? advanceYuragiAgents(state.yuragi, [], { steps: yuragiSteps, coupling })
          : state.yuragi,
        yuragiClockMs: clock % 3000,
      };
    }

    case 'FINISH_REVEAL':
      return state.outcome
        ? {
            ...state,
            phase: 'aftermath',
            chapter: 'verdict',
            location: 'court',
            overlay: 'none',
            profile: rewardProfile(state.profile, state.outcome),
            actionLog: record(state, 'finish-reveal'),
            yuragi: evolve(state, [{
              targets: 'all',
              kind: 'outcome',
              label: `Final verdict entered: ${state.outcome.verdict}`,
              actorId: 'judge',
              subject: CASE_001.id,
              predicate: 'verdict',
              value: state.outcome.verdict,
              intensity: 0.76,
            }], YURAGI_COUPLING.aftermath),
          }
        : state;

    case 'SET_PLAYER':
      return { ...state, player: action.player };

    case 'TOGGLE_MUTE':
      return { ...state, muted: !state.muted };

    case 'SET_AUDIO_LEVEL':
      return {
        ...state,
        muted: action.channel === 'master' && action.value > 0 ? false : state.muted,
        audio: {
          ...state.audio,
          [action.channel]: Math.max(0, Math.min(1, action.value)),
        },
      };

    case 'TOGGLE_SUBTITLES':
      return { ...state, audio: { ...state.audio, subtitles: !state.audio.subtitles } };

    case 'TOGGLE_REDUCED_MOTION':
      return { ...state, reducedMotion: !state.reducedMotion };

    case 'LOAD_STATE':
      return action.state.schemaVersion === 4 ? action.state : state;

    case 'RESET_CASE': {
      const fresh = createInitialState();
      return {
        ...fresh,
        profile: state.profile,
        audio: state.audio,
        muted: state.muted,
        reducedMotion: state.reducedMotion,
      };
    }

    default:
      return state;
  }
}
